import { Fragment, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { GRID_BREAKPOINT_LARGE } from 'HubStyleTokens/sizes';
import { H4 } from 'UIComponents/elements/headings';
import UIBox from 'UIComponents/layout/UIBox';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIClickable from 'UIComponents/button/UIClickable';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIImage from 'UIComponents/image/UIImage';
import UILightbox from 'UIComponents/lightbox/UILightbox';
import UISection from 'UIComponents/section/UISection';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FEATURE_IMAGE_MARGIN = 56;
const StyledFlex = styled(UIFlex).withConfig({
  displayName: "AssetFeatures__StyledFlex",
  componentId: "sc-3ol3z9-0"
})(["@media (max-width:", "){flex-wrap:wrap;}> *{@media (max-width:", "){flex-basis:100%;}}> .asset-feature__text{order:", ";}> .asset-feature__image{order:", ";margin-right:", "px;margin-left:", "px;@media (max-width:", "){margin:16px 0 0;order:3;}}"], GRID_BREAKPOINT_LARGE, GRID_BREAKPOINT_LARGE, props => props.alternate ? 2 : 1, props => props.alternate ? 1 : 2, props => props.alternate ? FEATURE_IMAGE_MARGIN : 0, props => props.alternate ? 0 : FEATURE_IMAGE_MARGIN, GRID_BREAKPOINT_LARGE);
export default function AssetFeatures({
  features,
  onTrackInteraction
}) {
  const [lightboxFeatureIndex, setLightboxFeatureIndex] = useState(null);
  const {
    featureImageWidth
  } = useResponsiveProps({
    featureImageWidth: {
      xs: '100%',
      lg: 350
    }
  });
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'features'
    }));
  }, [onTrackInteraction]);
  useEffect(() => {
    if (lightboxFeatureIndex != null) {
      const closeButton = document.querySelector('[role="button"][data-action="close"]');
      if (closeButton) {
        closeButton.focus();
      }
    }
  }, [lightboxFeatureIndex]);
  const renderLightboxFeature = useCallback(() => {
    if (lightboxFeatureIndex === null) {
      return null;
    }
    const {
      headline,
      imageUrl,
      text
    } = features[lightboxFeatureIndex];
    return /*#__PURE__*/_jsx(UILightbox, {
      imageAlt: headline,
      imageSrc: imageUrl,
      onClose: () => {
        setLightboxFeatureIndex(null);
        trackInteraction('close feature image lightbox');
      },
      children: /*#__PURE__*/_jsx(UIButtonWrapper, {
        children: /*#__PURE__*/_jsx("span", {
          children: text
        })
      })
    });
  }, [features, lightboxFeatureIndex, trackInteraction]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [features.map((feature, index) => {
      const {
        headline,
        imageUrl,
        text
      } = feature;
      const alternate = !(index % 2);
      const renderedFeatureImage = /*#__PURE__*/_jsx(UIClickable, {
        onClick: () => {
          setLightboxFeatureIndex(index);
          trackInteraction('click feature image', {
            itemIndex: index
          });
        },
        children: /*#__PURE__*/_jsx(UIImage, {
          alt: headline,
          src: imageUrl,
          width: featureImageWidth
        })
      });
      return /*#__PURE__*/_jsx(UISection, {
        className: "m-bottom-10",
        children: /*#__PURE__*/_jsxs(StyledFlex, {
          align: "center",
          alternate: alternate,
          children: [/*#__PURE__*/_jsxs(UIBox, {
            className: "asset-feature__text",
            grow: 1,
            shrink: 1,
            children: [/*#__PURE__*/_jsx(H4, {
              className: "is--heading-5",
              children: /*#__PURE__*/_jsx(UIBreakString, {
                children: headline
              })
            }), /*#__PURE__*/_jsx(UIBreakString, {
              children: text
            })]
          }), /*#__PURE__*/_jsx(UIBox, {
            basis: featureImageWidth,
            className: "asset-feature__image",
            grow: 0,
            shrink: 0,
            children: renderedFeatureImage
          })]
        })
      }, `feature-section-${index}`);
    }), renderLightboxFeature()]
  });
}